import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
} from "@mui/material";
import { HiOutlineX, HiOutlineCheck } from "react-icons/hi";
import { useEffect, useState } from "react";
import { updateService } from "API/services.api";

export default function EditServiceModal({
  open,
  onClose,
  serviceData,
  onServiceUpdated, // 👈 callback from parent
}) {
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    time_taken: "",
    status: "active",
  });

  useEffect(() => {
    if (!open || !serviceData) return;

    setForm({
      name: serviceData.name ?? "",
      description: serviceData.description ?? "",
      price: serviceData.price ?? "",
      time_taken: serviceData.time_taken ?? "",
      status: serviceData.status ?? "active",
    });
  }, [open, serviceData]);

  const onChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    if (!serviceData?.id) return;

    try {
      setSaving(true);

      const response = await updateService(serviceData.id, form);

      // 🔥 inform parent to update list
      onServiceUpdated(response.service);
      

      onClose();
    } catch (err) {
      console.error(err);
      alert("Failed to update service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ p: 2 }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Your Service
          </h2>
          <IconButton onClick={onClose}>
            <HiOutlineX />
          </IconButton>
        </div>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Service Name
            </label>
            <input
              value={form.name}
              onChange={onChange("name")}
              className="mt-2 w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              rows={4}
              value={form.description}
              onChange={onChange("description")}
              className="mt-2 w-full resize-none rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Price
              </label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  $
                </span>
                <input
                  value={form.price}
                  onChange={onChange("price")}
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 pl-7 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Estimated Time (mins)
              </label>
              <input
                value={form.time_taken}
                onChange={onChange("time_taken")}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>
        </div>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <div className="flex w-full justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <HiOutlineX className="text-base" />
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 rounded-lg bg-green-600 px-5 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            <HiOutlineCheck className="text-base" />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
